/**
 * Admin Management Page
 *
 * Admin-only page for managing organization settings and facilities.
 * Only accessible to users with 'admin' role.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import { organizationService, FacilityCreate, FacilityListItem, OrgUser } from '../../services/organizationService';
import { authService } from '../../services/authService';

// Get refreshFacilities from context to update header after changes

// Facility types
const FACILITY_TYPES = [
  'office',
  'manufacturing',
  'warehouse',
  'retail',
  'data_center',
  'distribution_center',
  'research_facility',
];

// Electricity grid options
const ELECTRICITY_GRIDS = [
  { value: 'US-WECC', label: 'US - Western (WECC)' },
  { value: 'US-RFC', label: 'US - RFC' },
  { value: 'US-SERC', label: 'US - Southeast (SERC)' },
  { value: 'US-MRO', label: 'US - Midwest (MRO)' },
  { value: 'US-NPCC', label: 'US - Northeast (NPCC)' },
  { value: 'US-TRE', label: 'US - Texas (TRE)' },
  { value: 'DE', label: 'Germany' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'AU', label: 'Australia' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
];

// Country options
const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'AU', label: 'Australia' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'CA', label: 'Canada' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminManagement: React.FC = () => {
  const { user, refreshFacilities } = useApp();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Facilities state
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<FacilityListItem | null>(null);
  const [facilityForm, setFacilityForm] = useState<FacilityCreate>({
    name: '',
    facility_type: 'office',
    address_street: '',
    address_city: '',
    address_country: 'US',
    address_postal_code: '',
    electricity_grid: 'US-WECC',
    floor_area_sqm: undefined,
    employee_count: undefined,
    operational_status: 'active',
  });

  // Users state
  const [users, setUsers] = useState<OrgUser[]>([]);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<FacilityListItem | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Invite user dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'viewer' });

  // Get organization ID from user
  const organizationId = user?.companyId;

  // Load data on mount
  useEffect(() => {
    if (organizationId) {
      loadFacilities();
      loadUsers();
    }
  }, [organizationId]);

  const loadFacilities = async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await organizationService.listFacilities(organizationId);
      setFacilities(response.facilities || []);
    } catch (err: any) {
      console.error('Failed to load facilities:', err);
      setError(err?.message || 'Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!organizationId) return;

    try {
      const response = await organizationService.listOrgUsers(organizationId);
      setUsers(response.users || []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Facility CRUD operations
  const handleOpenFacilityDialog = async (facility?: FacilityListItem) => {
    if (facility && organizationId) {
      setEditingFacility(facility);
      // Fetch full facility details to populate all fields correctly
      try {
        const detail = await organizationService.getFacility(organizationId, facility.id);
        setFacilityForm({
          name: detail.name,
          facility_type: detail.type,
          address_street: detail.address?.street || '',
          address_city: detail.address?.city || '',
          address_country: detail.address?.country || 'US',
          address_postal_code: detail.address?.postal_code || '',
          electricity_grid: detail.electricity_grid || 'US-WECC',
          floor_area_sqm: detail.floor_area_sqm || undefined,
          employee_count: detail.employee_count || undefined,
          operational_status: detail.operational_status || 'active',
        });
      } catch {
        // Fallback to list data if detail fetch fails
        setFacilityForm({
          name: facility.name,
          facility_type: facility.type,
          address_street: '',
          address_city: '',
          address_country: facility.country || 'US',
          address_postal_code: '',
          electricity_grid: facility.electricity_grid || 'US-WECC',
          floor_area_sqm: undefined,
          employee_count: undefined,
          operational_status: facility.status || 'active',
        });
      }
    } else {
      setEditingFacility(null);
      setFacilityForm({
        name: '',
        facility_type: 'office',
        address_street: '',
        address_city: '',
        address_country: 'US',
        address_postal_code: '',
        electricity_grid: 'US-WECC',
        floor_area_sqm: undefined,
        employee_count: undefined,
        operational_status: 'active',
      });
    }
    setFacilityDialogOpen(true);
  };

  const handleCloseFacilityDialog = () => {
    setFacilityDialogOpen(false);
    setEditingFacility(null);
    setError(null);
  };

  const handleFacilityFormChange = (field: keyof FacilityCreate, value: any) => {
    setFacilityForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveFacility = async () => {
    if (!organizationId) return;

    // Validation
    if (!facilityForm.name.trim()) {
      setError('Facility name is required');
      return;
    }
    if (!facilityForm.electricity_grid) {
      setError('Electricity grid is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingFacility) {
        // Update existing facility
        await organizationService.updateFacility(
          organizationId,
          editingFacility.id,
          facilityForm
        );
        setSuccess('Facility updated successfully');
      } else {
        // Create new facility
        await organizationService.createFacility(organizationId, facilityForm);
        setSuccess('Facility created successfully');
      }
      handleCloseFacilityDialog();
      loadFacilities();
      // Refresh the facilities in the context (for header dropdown)
      refreshFacilities();
    } catch (err: any) {
      console.error('Failed to save facility:', err);
      setError(err?.message || 'Failed to save facility');
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (facility: FacilityListItem) => {
    setFacilityToDelete(facility);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFacilityToDelete(null);
    setDeleteConfirmText('');
  };

  // Actually delete the facility after confirmation
  const handleConfirmDelete = async () => {
    if (!organizationId || !facilityToDelete) return;
    if (deleteConfirmText !== 'DELETE') return;

    setLoading(true);
    setError(null);

    try {
      await organizationService.deleteFacility(organizationId, facilityToDelete.id);
      setSuccess(`Facility "${facilityToDelete.name}" and all associated data deleted successfully`);
      handleCloseDeleteDialog();
      loadFacilities();
      // Refresh the facilities in the context (for header dropdown)
      refreshFacilities();
    } catch (err: any) {
      console.error('Failed to delete facility:', err);
      setError(err?.message || 'Failed to delete facility');
    } finally {
      setLoading(false);
    }
  };

  // Invite user handlers
  const handleOpenInviteDialog = () => {
    setInviteForm({ email: '', name: '', role: 'viewer' });
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setError(null);
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email.trim()) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.inviteUser({
        email: inviteForm.email.trim().toLowerCase(),
        name: inviteForm.name.trim() || undefined,
        role: inviteForm.role,
      });
      setSuccess(`User ${inviteForm.email} invited successfully`);
      handleCloseInviteDialog();
      loadUsers();
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      setError(err?.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied. This page is only accessible to administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Administration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization settings, facilities, and users.
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{facilities.length}</Typography>
                  <Typography color="text.secondary">Facilities</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h4">{users.length}</Typography>
                  <Typography color="text.secondary">Users</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4">
                    {facilities.filter(f => f.status === 'active').length}
                  </Typography>
                  <Typography color="text.secondary">Active Facilities</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Facilities" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Users" icon={<PeopleIcon />} iconPosition="start" />
        </Tabs>

        {/* Facilities Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Facilities</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenFacilityDialog()}
              >
                Add Facility
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : facilities.length === 0 ? (
              <Alert severity="info">
                No facilities found. Click "Add Facility" to create your first facility.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facilities.map((facility) => (
                      <TableRow key={facility.id} hover>
                        <TableCell>{facility.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={facility.type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{facility.country}</TableCell>
                        <TableCell>
                          <Chip
                            label={facility.status}
                            size="small"
                            color={facility.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenFacilityDialog(facility)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteDialog(facility)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Organization Users</Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenInviteDialog}
              >
                Invite User
              </Button>
            </Box>

            {users.length === 0 ? (
              <Alert severity="info">
                No users found in your organization.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((orgUser) => (
                      <TableRow key={orgUser.id} hover>
                        <TableCell>{orgUser.name}</TableCell>
                        <TableCell>{orgUser.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={orgUser.role}
                            size="small"
                            color={orgUser.role === 'admin' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={orgUser.status}
                            size="small"
                            color={orgUser.status === 'active' ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Facility Dialog */}
      <Dialog
        open={facilityDialogOpen}
        onClose={handleCloseFacilityDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFacility ? 'Edit Facility' : 'Add New Facility'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facility Name"
                value={facilityForm.name}
                onChange={(e) => handleFacilityFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Facility Type"
                value={facilityForm.facility_type}
                onChange={(e) => handleFacilityFormChange('facility_type', e.target.value)}
                required
              >
                {FACILITY_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={facilityForm.address_street}
                onChange={(e) => handleFacilityFormChange('address_street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={facilityForm.address_city}
                onChange={(e) => handleFacilityFormChange('address_city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Country"
                value={facilityForm.address_country}
                onChange={(e) => handleFacilityFormChange('address_country', e.target.value)}
                required
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.value} value={country.value}>
                    {country.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={facilityForm.address_postal_code}
                onChange={(e) => handleFacilityFormChange('address_postal_code', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Electricity Grid"
                value={facilityForm.electricity_grid}
                onChange={(e) => handleFacilityFormChange('electricity_grid', e.target.value)}
                required
                helperText="Used for Scope 2 emission calculations"
              >
                {ELECTRICITY_GRIDS.map((grid) => (
                  <MenuItem key={grid.value} value={grid.value}>
                    {grid.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Floor Area (sqm)"
                value={facilityForm.floor_area_sqm || ''}
                onChange={(e) => handleFacilityFormChange('floor_area_sqm', e.target.value ? Number(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Employee Count"
                value={facilityForm.employee_count || ''}
                onChange={(e) => handleFacilityFormChange('employee_count', e.target.value ? Number(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Operational Status"
                value={facilityForm.operational_status}
                onChange={(e) => handleFacilityFormChange('operational_status', e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="under_construction">Under Construction</MenuItem>
                <MenuItem value="decommissioned">Decommissioned</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFacilityDialog}>Cancel</Button>
          <Button
            onClick={handleSaveFacility}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (editingFacility ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pre-provision a user with a role. When they sign up and log in, they will automatically be assigned to your organization.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                helperText="Optional — will use email prefix if not provided"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Role"
                value={inviteForm.role}
                onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="analyst">Analyst</MenuItem>
                <MenuItem value="contributor">Contributor</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={loading || !inviteForm.email.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
          >
            {loading ? 'Inviting...' : 'Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          Delete Facility
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            This action is permanent and cannot be undone!
          </Alert>

          <Typography variant="body1" gutterBottom>
            You are about to delete the facility:
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            "{facilityToDelete?.name}"
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 3, color: 'text.secondary' }}>
            <li>The facility and all its configuration</li>
            <li>All emissions data (Scope 1, 2, 3) linked to this facility</li>
            <li>All water, waste, and energy data linked to this facility</li>
            <li>All social data linked to this facility</li>
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            To confirm deletion, type <strong>DELETE</strong> in the field below:
          </Typography>
          <TextField
            fullWidth
            placeholder="Type DELETE to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
            helperText={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE' ? 'Please type DELETE exactly' : ''}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== 'DELETE' || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
